import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { Patient, Organization, Encounter } from 'fhir/r4';
import { AuthService } from './auth/auth.service';
import { PdfService } from './pdf.service';
import { ReferenceDisplayService } from './reference-display.service';
import { backendEndPointToken } from '../app.config';
import { LoaderService } from '../loader/loader.service';

@Injectable({ providedIn: 'root' })
export class InvoicePrintService {
    private auth = inject(AuthService);
    private pdf = inject(PdfService);
    private refDisplay = inject(ReferenceDisplayService);
    private http = inject(HttpClient);
    private backend = inject(backendEndPointToken);
    private loaderService = inject(LoaderService);

    async printEncounterInvoice(
        encounterId: string,
        patientId: string,
        docType: string,
        tableDataSourceMap: Map<string, any>,
        overallTotalDisplay: string,
        overallPaidDisplay: string
    ): Promise<void> {
        const loaderRef = this.loaderService.openLoader('Generating PDF... please wait.', '');
        try {
            await this._printEncounterInvoice(encounterId, patientId, docType, tableDataSourceMap, overallTotalDisplay, overallPaidDisplay);
        } finally {
            if (loaderRef) loaderRef.close();
        }
    }

    private async _printEncounterInvoice(
        encounterId: string,
        patientId: string,
        docType: string,
        tableDataSourceMap: Map<string, any>,
        overallTotalDisplay: string,
        overallPaidDisplay: string
    ): Promise<void> {
        const hospitalId = (this.auth.user.getValue() as any)?.hospitalId || '';

        // Try to fetch patient and organization details for header
        let patientRefDisplay = patientId;
        let patientResource: Patient | null = null;
        let orgResource: Organization | null = null;
        let encounterResource: any = null;

        try {
            // First resolve Patient name if patientId is a FHIR id or reference
            const refToResolve = patientId.includes('/') ? patientId : `Patient/${patientId}`;
            patientRefDisplay = await firstValueFrom(this.refDisplay.ensure(refToResolve));

            patientResource = await firstValueFrom(this.http.get<Patient>(`${this.backend}/${refToResolve}`));
            try {
                encounterResource = await firstValueFrom(this.http.get<any>(`${this.backend}/Encounter/${encounterId}`));
            } catch (e) {}
            if (hospitalId) {
                try {
                    orgResource = await firstValueFrom(this.http.get<any>(`${this.backend}/Organization/${hospitalId}`));
                } catch (e) {
                    const orgs = await firstValueFrom(this.http.get<any>(`${this.backend}/Organization?identifier=${hospitalId}`));
                    if (orgs?.entry?.length) {
                        orgResource = orgs.entry[0].resource;
                    }
                }
            }
        } catch (e) {
            console.error('Failed to fetch patient or organization details for invoice printing', e);
        }

        const patientDetails = this.buildPatientDetails(patientResource, patientRefDisplay);
        const orgDetails = await this.buildOrganizationDetails(orgResource, hospitalId);

        const content: any[] = [];

        if (orgDetails) {
            content.push(this.buildModernOrgHeader(orgDetails));
        }

        content.push({ text: docType, style: 'header', margin: [0, 0, 0, 6] });
        content.push({ canvas: [{ type: 'line', x1: 0, y1: 5, x2: 515, y2: 5, lineWidth: 1 }], margin: [0, 0, 0, 20] });

        let encIdentifier = encounterId;
        if (encounterResource?.identifier) {
            const iden = encounterResource.identifier.find((i: any) => i.system === 'https://elikita-server.daalitech.com/fhir/identifier/enc');
            if (iden) encIdentifier = iden.value || encounterId;
        }

        content.push({
            columns: [
                this.buildPatientInfoBox(patientDetails),
                {
                    width: '*',
                    stack: [
                        { text: 'ENCOUNTER DETAILS', style: 'label', alignment: 'right' },
                        { text: `Encounter ID: ${encIdentifier}`, style: 'smallValue', alignment: 'right' },
                        { text: `Date: ${new Date().toLocaleString()}`, style: 'smallValue', alignment: 'right' }
                    ],
                    margin: [20, 0, 0, 0]
                }
            ],
            margin: [0, 0, 0, 30]
        });

        // Add Tables for each section
        for (const [name, ds] of tableDataSourceMap.entries()) {
            const rows = (ds?.data ?? []) as any[];
            if (rows.length === 0) continue;

            const tableBody: any[][] = [
                [{ text: 'Date', style: 'tableHeader' }, { text: 'Status', style: 'tableHeader' }, { text: 'Item', style: 'tableHeader' }, { text: 'Price', style: 'tableHeader', alignment: 'right' }, { text: 'Qty', style: 'tableHeader', alignment: 'right' }, { text: 'Total', style: 'tableHeader', alignment: 'right' }]
            ];

            let sectionTotalPaid = 0;
            let sectionTotalGross = 0;
            let currency = 'NGN';

            for (const r of rows) {
                const date = r.invoiceDateDisplay ?? '';
                const status = r.invoiceStatusDisplay ?? r.status ?? 'N/A';
                let label = r.medicationDisplay || r.serviceDisplay || 'N/A';

                // Resolve placeholder for lab/imaging/procedure to get the original ServiceRequest
                const ci = r.relatedChargeItem;
                if (ci) {
                    const serviceRef = ci.service?.[0]?.reference;
                    if (serviceRef && (serviceRef.startsWith('Observation/') || serviceRef.startsWith('ImagingStudy/') || serviceRef.startsWith('Procedure/'))) {
                        try {
                            const placeholder = await firstValueFrom(this.http.get<any>(`${this.backend}/${serviceRef}`));
                            const basedOnRef = placeholder?.basedOn?.[0]?.reference;
                            if (basedOnRef && basedOnRef.startsWith('ServiceRequest/')) {
                                label = basedOnRef;
                            }
                        } catch (e) {
                            console.error('Failed to resolve placeholder resource for invoice print', e);
                        }
                    }
                }

                const unitPriceVal = r?.unitPriceValue != null ? Number(r.unitPriceValue) : 0;
                currency = r?.currency || currency;
                const unitPriceDisplay = new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(unitPriceVal);

                const qty = r.quantityDisplay ?? 'N/A';

                const totalGrossVal = r?.totalGross?.value != null ? Number(r.totalGross.value) : 0;
                const totalGrossDisplay = r.totalGrossDisplay ?? new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(totalGrossVal);

                if (status.toLowerCase() === 'balanced') {
                    sectionTotalPaid += totalGrossVal;
                }
                sectionTotalGross += totalGrossVal;
                
                let itemContent: any = { text: label, style: 'bodyText' };
                const stackItems: any[] = [];
                
                if (label && label.includes('/') && label.split('/').length === 2 && !label.includes(' ')) {
                    try {
                        const originalLabel = label;
                        const resolvedIdentifier = await firstValueFrom(this.refDisplay.ensureIdentifier(originalLabel));
                        label = await firstValueFrom(this.refDisplay.ensure(originalLabel));
                        
                        stackItems.push({ text: label, style: 'bodyText' });
                        
                        // If it successfully resolved an identifier that isn't just the fallback reference string
                        if (resolvedIdentifier && resolvedIdentifier !== originalLabel) {
                            stackItems.push({ text: resolvedIdentifier, style: 'bodySubText' });
                        }
                    } catch (e) {
                        console.error('Failed to resolve item reference for PDF printing', label, e);
                        stackItems.push({ text: label, style: 'bodyText' });
                    }
                } else {
                    stackItems.push({ text: label, style: 'bodyText' });
                }

                // If this is a medication, see if we can resolve the Dispense ID
                if (r.medicationDisplay) {
                    const serviceRef = ci?.service?.[0]?.reference;
                    if (serviceRef && serviceRef.toLowerCase().includes('medicationdispense')) {
                        try {
                            const dispId = await firstValueFrom(this.refDisplay.ensureIdentifier(serviceRef));
                            if (dispId && dispId !== serviceRef) {
                                stackItems.push({ text: `Dispense ID: ${dispId}`, style: 'bodySubText' });
                            }
                        } catch (e) {}
                    }
                }

                itemContent = stackItems.length > 1 ? { stack: stackItems } : stackItems[0];

                tableBody.push([
                    { text: date, style: 'bodyText' },
                    { text: status, style: 'bodyText' },
                    itemContent,
                    { text: unitPriceDisplay, style: 'bodyText', alignment: 'right' },
                    { text: qty, style: 'bodyText', alignment: 'right' },
                    { text: totalGrossDisplay, style: 'bodyText', alignment: 'right' }
                ]);
            }

            const formatMoney = (val: number) => new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(val);

            // Add section footer
            tableBody.push([
                { text: 'Section totals', style: 'tableFooter', colSpan: 4 }, '', '', '',
                { text: `Paid: ${formatMoney(sectionTotalPaid)}`, style: 'tableFooter', alignment: 'right' },
                { text: formatMoney(sectionTotalGross), style: 'tableFooter', alignment: 'right' }
            ]);

            content.push({
                stack: [
                    { text: name, style: 'tableTitle' },
                    {
                        table: {
                            headerRows: 1,
                            widths: [70, 60, '*', 70, 30, 80],
                            body: tableBody
                        },
                        layout: 'lightHorizontalLines',
                        margin: [0, 0, 0, 20]
                    }
                ]
            });
        }

        // Overall Totals
        content.push({
            columns: [
                { width: '*', text: '' },
                {
                    width: 250,
                    table: {
                        widths: ['*', 100],
                        body: [
                            [
                                { text: 'Overall Paid:', style: 'totalLabel', alignment: 'right' },
                                { text: overallPaidDisplay, style: 'totalValue', alignment: 'right' }
                            ],
                            [
                                { text: 'Overall Total:', style: 'totalLabel', alignment: 'right' },
                                { text: overallTotalDisplay, style: 'totalValue', alignment: 'right' }
                            ]
                        ]
                    },
                    layout: 'noBorders'
                }
            ],
            margin: [0, 20, 0, 0]
        });

        content.push({ text: `Generated: ${new Date().toLocaleString()}`, style: 'footerNote', margin: [0, 40, 0, 0], alignment: 'right' });

        const docDefinition = {
            pageSize: 'A4',
            pageMargins: [32, 40, 32, 40],
            content,
            styles: {
                header: { fontSize: 20, bold: true, color: '#2c3e50' },
                subheader: { fontSize: 10, color: '#7f8c8d' },
                label: { fontSize: 8, bold: true, color: '#95a5a6', marginBottom: 2 },
                nameValue: { fontSize: 12, bold: true, marginBottom: 2 },
                smallValue: { fontSize: 10, marginBottom: 2 },
                tableTitle: { fontSize: 12, bold: true, color: '#2c3e50', margin: [0, 16, 0, 6] },
                tableHeader: { fontSize: 10, bold: true, color: '#2c3e50', margin: [0, 4, 0, 4] },
                bodyText: { fontSize: 10, lineHeight: 1.2, margin: [0, 2, 0, 2] },
                bodySubText: { fontSize: 8, color: '#7f8c8d', margin: [0, 1, 0, 2] },
                tableFooter: { fontSize: 10, bold: true, margin: [0, 4, 0, 4] },
                totalLabel: { fontSize: 12, bold: true, color: '#2c3e50' },
                totalValue: { fontSize: 12, bold: true },
                footerNote: { fontSize: 9, color: '#7f8c8d' }
            },
            defaultStyle: { font: 'Roboto', fontSize: 11 }
        };

        try {
            this.pdf.print(docDefinition);
        } catch (e: any) {
            console.error('Error generating PDF:', e);
// $#$            //      /* alert('Error generating PDF: ' + (e?.message || e) */);
        }
    }

    private buildPatientDetails(patient: Patient | null, fallbackDisplay: string) {
        const name = patient?.name?.[0] ? `${patient.name[0].given?.join(' ')} ${patient.name[0].family || ''}` : fallbackDisplay;
        const reference = patient?.id ? `Patient/${patient.id}` : fallbackDisplay;
        const gender = patient?.gender ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1) : '';
        
        let ageText = '';
        if (patient?.birthDate) {
            const bd = new Date(patient.birthDate);
            if (!isNaN(bd.getTime())) {
                const ageDifMs = Date.now() - bd.getTime();
                const ageDate = new Date(ageDifMs);
                const age = Math.abs(ageDate.getUTCFullYear() - 1970);
                ageText = `${age} yrs`;
            }
        }

        let hospitalId = '';
        let patientPid = '';
        if (patient?.identifier) {
            const hId = patient.identifier.find(i => i.system?.includes('hospitalId') || i.type?.text?.toLowerCase().includes('hospital'));
            if (hId) hospitalId = hId.value || '';
            const pId = patient.identifier.find(i => i.system === 'https://elikita-server.daalitech.com/fhir/identifier/pid');
            if (pId) patientPid = pId.value || '';
        }

        return { name, reference, hospitalId, patientPid, gender, age: ageText };
    }

    private async buildOrganizationDetails(org: Organization | null, fallbackHospitalId: string) {
        const name = org?.name || 'Healthcare Facility';
        let address = '';
        if (org?.address?.length) {
            const a = org.address[0];
            address = [a.line?.join(' '), a.city, a.state].filter(Boolean).join(', ');
        }
        let telecom = '';
        if (org?.telecom?.length) {
            telecom = org.telecom.map(t => t.value).filter(Boolean).join(', ');
        }
        const logo = await this.resolveOrganizationLogo(org);
        return { name, address, telecom, logo, hospitalId: fallbackHospitalId };
    }

    private buildModernOrgHeader(details: { name?: string; address?: string; telecom?: string; logo?: string | null; hospitalId?: string | null }) {
        const rightStack = [
            details.name ? { text: details.name, style: 'header', alignment: 'right' } : null,
            details.address ? { text: details.address, style: 'subheader', alignment: 'right', opacity: 0.6 } : null,
            details.telecom ? { text: details.telecom, style: 'subheader', alignment: 'right', opacity: 0.6 } : null,
            details.hospitalId ? { text: `Hospital ID: ${details.hospitalId}`, style: 'subheader', alignment: 'right', opacity: 0.6 } : null
        ].filter(Boolean);

        return {
            columns: [
                details.logo
                    ? { image: details.logo, width: 60, alignment: 'left' }
                    : { width: 60, text: '' },
                { stack: rightStack.length ? rightStack : [{ text: '', alignment: 'right' }] }
            ],
            margin: [0, 0, 0, 10]
        };
    }

    private async resolveOrganizationLogo(org?: Organization | null): Promise<string | null> {
        const source = this.extractOrganizationLogoSource(org);
        if (!source) {
            return null;
        }

        if (source.startsWith('data:')) {
            return source;
        }

        return await this.fetchImageAsDataUrl(source);
    }

    private extractOrganizationLogoSource(org?: Organization | null): string | null {
        if (!org) {
            return null;
        }

        const photo = (org as any)?.photo?.[0];
        if (photo?.data) {
            return this.composeDataUrl(photo.data, photo.contentType);
        }

        const extensions = Array.isArray(org.extension) ? org.extension : [];
        const match = extensions.find((ext: any) => this.isOrganizationLogoExtension(ext?.url));
        if (!match) {
            return null;
        }

        if (match.valueString && match.valueString.startsWith('data:')) {
            return match.valueString;
        }
        if (match.valueUrl) {
            return match.valueUrl;
        }

        const attachment = match.valueAttachment;
        if (attachment?.data) {
            return this.composeDataUrl(attachment.data, attachment.contentType);
        }
        if (attachment?.url) {
            return attachment.url;
        }
        if (attachment?.title && attachment.title.startsWith('data:')) {
            return attachment.title;
        }

        return null;
    }

    private isOrganizationLogoExtension(url?: string | null): boolean {
        const normalized = (url || '').toLowerCase();
        if (!normalized) {
            return false;
        }
        return normalized.endsWith('/structuredefinition/organizationlogo') || normalized.includes('organizationlogo');
    }

    private composeDataUrl(base64Data: string, contentType?: string): string {
        const safeContentType = (contentType || 'image/png').trim() || 'image/png';
        return `data:${safeContentType};base64,${base64Data}`;
    }

    private async fetchImageAsDataUrl(url: string): Promise<string | null> {
        if (typeof fetch !== 'function') {
            return null;
        }
        try {
            const response = await fetch(url);
            if (!response?.ok) {
                return null;
            }
            const blob = await response.blob();
            return await this.blobToDataUrl(blob);
        } catch {
            return null;
        }
    }

    private blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string) || '');
            reader.onerror = () => reject(new Error('Failed to read image blob.'));
            reader.readAsDataURL(blob);
        });
    }

    private buildPatientInfoBox(patient: { name?: string; reference?: string; hospitalId?: string | null; patientPid?: string | null; gender?: string; age?: string }) {
        return {
            width: 'auto',
            table: {
                widths: [200],
                body: [
                    [
                        {
                            stack: [
                                { text: 'PATIENT DETAILS', style: 'label' },
                                { text: patient?.name || 'N/A', style: 'nameValue' },
                                { text: patient?.patientPid ? `Patient ID: ${patient.patientPid}` : '', style: 'smallValue' },
                                { text: patient?.age ? `Age: ${patient.age}` : '', style: 'smallValue' },
                                { text: patient?.gender ? `Gender: ${patient.gender}` : '', style: 'smallValue' },
                                { text: patient?.hospitalId ? `Hospital ID: ${patient.hospitalId}` : '', style: 'smallValue' }
                            ].filter((entry) => !!entry?.text),
                            fillColor: '#f9f9f9',
                            margin: [10, 10, 10, 10]
                        }
                    ]
                ]
            },
            layout: 'lightHorizontalLines'
        };
    }
}
